import { generatedApi } from '@shared/api/generated-api';
import type { ChannelInfo, ChannelInfoForList } from '@shared/api';
import type { AppDispatch, RootState } from '@shared/redux/store';

const pushChannelToCache = (
  channel: ChannelInfo,
  organizationId: number,
  dispatch: AppDispatch,
  getState: () => RootState,
) => {
  const channelForList: ChannelInfoForList = {
    id: channel.id,
    name: channel.name,
    type: channel.type,
    is_active: channel.is_active,
    agent_id: channel.agent_id,
    agent_name: '',
    description: channel.description,
    created_at: channel.created_at,
  };

  const cachedArgs = generatedApi.util.selectCachedArgsForQuery(
    getState(),
    'getChannelListChannelListGet',
  );

  for (const args of cachedArgs) {
    if (args.organizationId !== organizationId) continue;

    dispatch(
      generatedApi.util.updateQueryData('getChannelListChannelListGet', args, (draft) => {
        const alreadyExists = draft.some((item) => item.id === channel.id);
        if (!alreadyExists) {
          draft.unshift(channelForList);
        }
      }),
    );
  }
};

const deleteChannelFromCache = (
  channelId: number,
  dispatch: AppDispatch,
  getState: () => RootState,
) => {
  const cachedArgs = generatedApi.util.selectCachedArgsForQuery(
    getState(),
    'getChannelListChannelListGet',
  );

  for (const args of cachedArgs) {
    dispatch(
      generatedApi.util.updateQueryData('getChannelListChannelListGet', args, (draft) => {
        const index = draft.findIndex((item) => item.id === channelId);
        if (index !== -1) {
          draft.splice(index, 1);
        }
      }),
    );
  }
};

export const channelsEndpoints = {
  getChannelListChannelListGet: {
    providesTags: ['channel'] as const,
  },

  channelHealthCheckChannelHealthPost: {
    invalidatesTags: [],
  },

  updateChannelChannelUpdatePost: {
    invalidatesTags: [],
    async onQueryStarted(queryArgs, { dispatch, queryFulfilled, getState }) {
      try {
        const { data } = await queryFulfilled;

        const getChannelListArgs = generatedApi.util.selectCachedArgsForQuery(
          getState() as RootState,
          'getChannelListChannelListGet',
        );

        for (const args of getChannelListArgs) {
          dispatch(
            generatedApi.util.updateQueryData('getChannelListChannelListGet', args, (draft) => {
              const channel = draft.find((item) => item.id === queryArgs.channelId);
              if (channel) {
                channel.name = data.name;
                channel.description = data.description;
                channel.is_active = data.is_active;
                channel.agent_id = data.agent_id;
                channel.type = data.type;
              }
            }),
          );
        }

        const getChannelInfoArgs = generatedApi.util.selectCachedArgsForQuery(
          getState() as RootState,
          'getChannelInfoChannelInfoGet',
        );

        for (const args of getChannelInfoArgs) {
          if (args.channelId !== queryArgs.channelId) continue;
          dispatch(
            generatedApi.util.updateQueryData('getChannelInfoChannelInfoGet', args, (draft) => {
              Object.assign(draft, data);
            }),
          );
        }
      } catch (error) {}
    },
  },

  createVkBotChannelChannelCreateVkBotPost: {
    invalidatesTags: [],
    async onQueryStarted(args, { queryFulfilled, dispatch, getState }) {
      try {
        const { data } = await queryFulfilled;
        pushChannelToCache(
          data,
          args.organizationId,
          dispatch as AppDispatch,
          getState as () => RootState,
        );
      } catch (error) {}
    },
  },

  createWidgetChannelChannelCreateWidgetPost: {
    invalidatesTags: [],
    async onQueryStarted(args, { queryFulfilled, dispatch, getState }) {
      try {
        const { data } = await queryFulfilled;
        pushChannelToCache(
          data,
          args.organizationId,
          dispatch as AppDispatch,
          getState as () => RootState,
        );
      } catch (error) {}
    },
  },

  createTelegramBotChannelChannelCreateTelegramBotPost: {
    invalidatesTags: [],
    async onQueryStarted(args, { queryFulfilled, dispatch, getState }) {
      try {
        const { data } = await queryFulfilled;
        pushChannelToCache(
          data,
          args.organizationId,
          dispatch as AppDispatch,
          getState as () => RootState,
        );
      } catch (error) {}
    },
  },

  createMaxBotChannelChannelCreateMaxBotPost: {
    invalidatesTags: [],
    async onQueryStarted(args, { queryFulfilled, dispatch, getState }) {
      try {
        const { data } = await queryFulfilled;
        pushChannelToCache(
          data,
          args.organizationId,
          dispatch as AppDispatch,
          getState as () => RootState,
        );
      } catch (error) {}
    },
  },

  createAvitoChannelChannelCreateAvitoPost: {
    invalidatesTags: [],
    async onQueryStarted(args, { queryFulfilled, dispatch, getState }) {
      try {
        const { data } = await queryFulfilled;
        pushChannelToCache(
          data,
          args.organizationId,
          dispatch as AppDispatch,
          getState as () => RootState,
        );
      } catch (error) {}
    },
  },

  deleteChannelChannelDeleteDelete: {
    invalidatesTags: [],
    async onQueryStarted(args, { queryFulfilled, dispatch, getState }) {
      try {
        await queryFulfilled;
        deleteChannelFromCache(
          args.channelId,
          dispatch as AppDispatch,
          getState as () => RootState,
        );
      } catch (error) {}
    },
  },
} satisfies Parameters<typeof generatedApi.enhanceEndpoints>[0]['endpoints'];
